import React, { useState, useRef, useEffect } from 'react';
import { Search, Mic, Moon, Sun, ShoppingBag, ArrowRight, ChevronLeft, ChevronRight, ShoppingCart, X, ShieldCheck, Loader2, LogOut } from 'lucide-react';
import { CategoryDoc, Product, Store, Ad, HomeSection, PageConfig } from '../types';
import { db, collection, query, where, orderBy, onSnapshot, getDocs, doc } from '../firebase';
import { ProfileModal } from './ProfileModal';
import { OptimizedImage } from './OptimizedImage';
import { CartView } from './CartView';
import { CategoryView } from './CategoryView';
import { ProductSection } from './ProductSection';
import { ProductDetailsModal } from './ProductDetailsModal';
import { StoreDetailsPage } from './StoreDetailsPage';
import { SearchPage } from './SearchPage';
import { Footer } from './Footer';
import { Toast } from './Toast';
import { User } from 'firebase/auth';
import { useUser } from '../contexts/UserContext';
import { useHomepage } from '../hooks/useFirestoreData';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

interface HomePageProps {
  isDark: boolean;
  toggleTheme: () => void;
  onLogout: () => void;
  user?: User | null;
}

type PageView = 'home' | 'cart' | 'category';

export const HomePage: React.FC<HomePageProps> = ({ isDark, toggleTheme, onLogout, user }) => {
  const {
    profile,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    toggleCartSelection
  } = useUser();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const isAdmin = profile?.role === "admin" || user?.email === "yousefkatato66@gmail.com";

  // Check if we're on a category route
  const isCategoryRoute = location.pathname.startsWith('/category/');
  const categoryIdFromRoute = params.id ? String(params.id) : null;

  // --- DATABASE CONNECTION ---
  const { featuredProducts, randomProducts, featuredShops, ads, loading: homeLoading } = useHomepage();

  const displayedAds = ads.filter(ad => ad.page === 'home' || ad.page === 'both' || !ad.page);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [pageView, setPageView] = useState<PageView>(() => {
    const saved = localStorage.getItem('currentPage');
    return (saved === 'home' || saved === 'cart' || saved === 'category') ? saved as PageView : 'home';
  });

  const [activeCategory, setActiveCategory] = useState<CategoryDoc | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [isTransitioningPage, setIsTransitioningPage] = useState(false);

  // Use cart from context profile
  const cartItems = profile?.cart || [];
  const cartItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  // Local state for user data to reflect realtime DB changes (from profile)
  const userAvatar = profile?.photoURL || user?.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300";
  const userName = profile?.name || user?.displayName || "مستخدم جديد";

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [addedToCartMap, setAddedToCartMap] = useState<{ [key: number]: boolean }>({});
  const [showToast, setShowToast] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  const footerRef = useRef<HTMLDivElement>(null);
  const [cartButtonOffset, setCartButtonOffset] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Drag to scroll state
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [homeSections, setHomeSections] = useState<HomeSection[]>([]);
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [scrollLeft, setScrollLeft] = useState(0);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHoveringHero, setIsHoveringHero] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const velocity = useRef(0);
  const frameId = useRef(0);
  const lastTime = useRef(0);
  const lastX = useRef(0);

  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);

  // Native Back Button Handling
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (selectedProduct) {
        setSelectedProduct(null);
        return;
      }
      if (selectedStore) {
        setSelectedStore(null);
        return;
      }
      if (pageView !== 'home') {
        setPageView('home');
        setActiveCategory(null);
        return;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedProduct, selectedStore, pageView]);

  const pushHistoryState = (stateName: string) => {
    window.history.pushState({ view: stateName }, '', window.location.href);
  };

  const handleLogoClick = () => {
    if (selectedProduct || selectedStore || activeCategory || pageView !== 'home') {
      // If we are in some sub-view, we should ideally go back in history, 
      // but if we want a "hard reset", we can just set state.
      // For consistency, let's just do a clean state reset plus navigate to root
      setSelectedProduct(null);
      setSelectedStore(null);
      setActiveCategory(null);
      setPageView('home');
      navigate('/', { replace: true });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const extendedCategories: any[] = [
    { id: "1", name: "الأكثر مبيعاً", image: "" },
    { id: "2", name: "العروض والخصومات", image: "" },
    { id: "3", name: "الملابس", image: "" },
    { id: "5", name: "الالكترونيات", image: "" },
    { id: "4", name: "أجهزة المنزل", image: "" },
    { id: "6", name: "الكتب", image: "" },
    { id: "7", name: "قريبا", image: "", isComingSoon: true },
  ];
  useEffect(() => {
    if (isHoveringHero || displayedAds.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displayedAds.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isHoveringHero, displayedAds.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % (displayedAds.length || 1));
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + (displayedAds.length || 1)) % (displayedAds.length || 1));
  const handleOpenCategory = (categoryId: string) => {
    const category = extendedCategories.find(c => c.id === categoryId);
    if (category && !category.isComingSoon) {
      navigate(`/category/${categoryId}`);
      setActiveCategory(category);
      handlePageChange('category');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleOpenStore = (store: Store) => {
    if (isDraggingRef.current) return;
    navigate(`/shop/${store.id}`);
  };

  // Handle category route and store/product query param
  useEffect(() => {
    // 1. Check for store query param (?store=ID)
    const urlParams = new URLSearchParams(location.search);
    const storeIdParam = urlParams.get('store');
    const productIdParam = urlParams.get('product');

    if (storeIdParam) {
      navigate(`/shop/${storeIdParam}`, { replace: true });
    }

    if (productIdParam) {
      navigate(`/product/${productIdParam}`, { replace: true });
    }

    if (isCategoryRoute && categoryIdFromRoute) {
      const category = extendedCategories.find(c => String(c.id) === categoryIdFromRoute);
      if (category && !category.isComingSoon) {
        setActiveCategory(category);
        setPageView('category');
        document.title = `${category.name} - Rashid Online`;
      } else {
        // If category not found, redirect to home
        navigate('/');
        setPageView('home');
      }
    } else if (location.pathname === '/search') {
      // Search page is handled separately
      document.title = 'البحث - Rashid Online';
    } else if (location.pathname === '/cart') {
      setPageView('cart');
      document.title = 'Rashid Online - سلة التسوق';
    } else if (pageView === 'home') {
      document.title = 'الصفحة الرئيسية - Rashid Online';
    }
  }, [isCategoryRoute, categoryIdFromRoute, location.pathname, location.search, navigate, allProducts]);

  // Handle navigation state from GlobalHeader / GlobalCart
  useEffect(() => {
    const state = location.state as { view?: PageView; clearModals?: boolean } | null;
    let didChange = false;

    if (state?.clearModals) {
      setSelectedStore(null);
      setSelectedProduct(null);
      didChange = true;
    }

    if (state?.view && state.view !== pageView) {
      setPageView(state.view);
      didChange = true;
    }

    if (didChange) {
      // Clear the state so it doesn't trigger again on reload
      window.history.replaceState({}, document.title);
    }
  }, [location.state, pageView]);

  useEffect(() => {
    localStorage.setItem('currentPage', pageView);
    if (pageView === 'cart') {
      document.title = 'Rashid Online - سلة التسوق';
      document.body.style.overflow = 'hidden';
    } else if (pageView === 'category' && activeCategory) {
      document.title = `${activeCategory.name} - Rashid Online`;
      document.body.style.overflow = 'auto';
    } else if (pageView === 'home') {
      document.title = 'الصفحة الرئيسية - Rashid Online';
      document.body.style.overflow = 'auto';
    }
  }, [pageView, activeCategory]);

  // Robust Scroll Restoration for HomePage
  useEffect(() => {
    // Restore scroll position when returning to home view
    if (pageView === 'home' && !selectedProduct && !selectedStore && !isCategoryRoute && location.pathname === '/') {
      const savedScroll = sessionStorage.getItem('homeScrollPos');
      if (savedScroll) {
        // Immediate restore then delay to catch dynamic images
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' as ScrollBehavior });
        });
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' as ScrollBehavior });
        }, 50);
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' as ScrollBehavior });
        }, 150);
      }
    }

    // Save scroll position dynamically whenever it changes
    let scrollTimeout: any;
    const handleScrollSave = () => {
      if (pageView === 'home' && !selectedProduct && !selectedStore && !isCategoryRoute && location.pathname === '/') {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          sessionStorage.setItem('homeScrollPos', window.scrollY.toString());
        }, 50);
      }
    };

    window.addEventListener('scroll', handleScrollSave, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScrollSave);
      clearTimeout(scrollTimeout);
    };
  }, [pageView, selectedProduct, selectedStore, isCategoryRoute, location.pathname]);


  useEffect(() => {
    // Subscribe to PageConfig
    const unsubConfig = onSnapshot(doc(db, 'pageConfigs', 'home_page'), (snap) => {
      if (snap.exists()) setPageConfig(snap.data() as PageConfig);
      else setPageConfig(null);
    });

    // Subscribe to Home Sections with error fallback
    const qSections = query(collection(db, 'homeSections'), where('isActive', '==', true), orderBy('order', 'asc'));
    let unsubSections = onSnapshot(qSections, (snapshot) => {
      const sectionsData: HomeSection[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as HomeSection;
        // Filter out sections with order 0
        if (data.order !== 0) {
          sectionsData.push({ id: doc.id, ...data });
        }
      });
      setHomeSections(sectionsData);
    }, (error) => {
      console.warn('Home sections query failed (missing index?), trying fallback:', error.message);
      // Fallback: fetch all homeSections without composite index
      unsubSections = onSnapshot(collection(db, 'homeSections'), (snapshot) => {
        const sectionsData: HomeSection[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as HomeSection;
          if (data.isActive && data.order !== 0) {
            sectionsData.push({ id: doc.id, ...data });
          }
        });
        // Sort client-side
        sectionsData.sort((a, b) => (a.order || 0) - (b.order || 0));
        setHomeSections(sectionsData);
      });
    });

    // Subscribe to all products for dynamic sections
    const unsubAllProducts = onSnapshot(collection(db, 'products'), (snap) => {
      const list: Product[] = [];
      snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as Product));
      setAllProducts(list);
    });

    // Subscribe to all stores for dynamic shops sections
    const unsubStores = onSnapshot(collection(db, 'stores'), (snap) => {
      const list: Store[] = [];
      snap.forEach(doc => {
        const data = doc.data() as Store;
        if (data.isActive !== false) list.push({ id: doc.id, ...data });
      });
      setAllStores(list);
    });

    return () => {
      unsubConfig();
      unsubSections();
      unsubAllProducts();
      unsubStores();
    };
  }, []);

  const handleNextSlide = () => {
    if (displayedAds.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % displayedAds.length);
  };

  const handlePrevSlide = () => {
    if (displayedAds.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + displayedAds.length) % displayedAds.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      handleNextSlide();
    }
    if (touchStartX.current - touchEndX.current < -50) {
      handlePrevSlide();
    }
  };

  const startInertia = () => {
    const inertia = () => {
      if (!scrollContainerRef.current) return;
      if (Math.abs(velocity.current) < 0.1) return;

      scrollContainerRef.current.scrollLeft -= velocity.current * 16;
      velocity.current *= 0.95; // Decay
      frameId.current = requestAnimationFrame(inertia);
    };
    cancelAnimationFrame(frameId.current);
    frameId.current = requestAnimationFrame(inertia);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsMouseDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    cancelAnimationFrame(frameId.current);

    isDraggingRef.current = false;
    dragStartXRef.current = e.pageX;
    lastX.current = e.pageX;
    lastTime.current = Date.now();
    velocity.current = 0;
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      setIsMouseDown(false);
      startInertia();
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    startInertia();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;

    // Detect drag
    if (Math.abs(e.pageX - dragStartXRef.current) > 5) {
      isDraggingRef.current = true;
    }

    // Velocity calculation
    const now = Date.now();
    const dt = now - lastTime.current;
    if (dt > 0) {
      const dx = e.pageX - lastX.current;
      velocity.current = dx / dt; // pixels per ms
      lastX.current = e.pageX;
      lastTime.current = now;
    }
  };

  const handlePageChange = (newView: PageView) => {
    if (newView !== 'home') {
      pushHistoryState(newView);
    }
    setIsTransitioningPage(true);
    setTimeout(() => {
      setPageView(newView);
      setIsTransitioningPage(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 400);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (footerRef.current && pageView === 'home' && !selectedProduct && !selectedStore) {
        const footerRect = footerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const overlap = windowHeight - footerRect.top;
        setCartButtonOffset(overlap > 0 ? overlap + 10 : 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageView, selectedProduct, selectedStore]);

  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();

    // Save scroll position
    setScrollPosition(window.scrollY);

    const success = await addToCart(product, {
      color: (product as any).selectedColor,
      size: (product as any).selectedSize
    });

    if (success) {
      // Show toast
      setShowToast(true);

      // Show temporary "Added" feedback
      setAddedToCartMap(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedToCartMap(prev => ({ ...prev, [product.id]: false }));
      }, 2000);

      // Auto scroll to related products if on product page
      if (selectedProduct) {
        setTimeout(() => {
          const relatedSection = document.querySelector('[data-related-products]');
          if (relatedSection) {
            relatedSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  };

  const openProductDetails = (product: Product) => {
    navigate(`/product/${product.id}`);
  };

  const closeProductDetails = () => {
    navigate(-1);
  };

  const closeProfile = () => {
    setShowProfileModal(false);
  };

  const handleBuyNow = (product: Product, quantity: number, color: string, size: string) => {
    const buyNowItem = {
      ...product,
      quantity,
      selectedColor: color,
      selectedSize: size,
      selected: true
    };
    navigate('/checkout', { state: { buyNowItem } });
  };

  const [systemSettings, setSystemSettings] = useState<any>(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'system_settings'), (snap) => {
      if (snap.exists()) setSystemSettings(snap.data());
    });
    return () => unsub();
  }, []);

  const defaultCashbackBanner = "https://github.com/yousefkatato11-creator/main/blob/main/%D9%83%D8%A7%D8%B4%20%D8%A8%D8%A7%D9%83%20(1).png?raw=true";
  const cashbackBannerSrc = isDark
    ? (systemSettings?.cashbackBannerDark || defaultCashbackBanner)
    : (systemSettings?.cashbackBannerLight || defaultCashbackBanner);

  const neonGlowStyle = isDark
    ? 'shadow-[0_0_8px_rgba(255,255,255,0.04)] md:shadow-[0_0_35px_rgba(255,255,255,0.15)] border-white/15'
    : 'shadow-[0_0_8px_rgba(37,99,235,0.15)] md:shadow-[0_0_45px_rgba(37,99,235,0.55)] border-blue-200';

  const headerNeonStyle = isDark
    ? 'shadow-[0_0_15px_rgba(255,255,255,0.04)] md:shadow-[0_0_25px_rgba(255,255,255,0.06)] border-white/10'
    : 'shadow-[0_0_20px_rgba(59,130,246,0.25)] md:shadow-[0_0_30px_rgba(59,130,246,0.35)] border-blue-200';

  // Render Content Logic
  const renderContent = () => {
    // Handle search route - don't render main content, SearchPage will handle it
    if (location.pathname === '/search') {
      return null;
    }

    if (pageView === 'cart') {
      return (
        <CartView
          cartItems={cartItems}
          isDark={isDark}
          onUpdateQuantity={updateCartQuantity}
          onRemove={removeFromCart}
          onToggleSelection={toggleCartSelection}
          onBack={() => {
            setPageView('home');
            navigate('/');
          }}
          onOpenDetails={openProductDetails}
        />
      );
    }
    if (pageView === 'category') {
      return (
        <CategoryView
          activeCategory={activeCategory}
          products={randomProducts}
          isDark={isDark}
          onOpenDetails={openProductDetails}
          onAddToCart={handleAddToCart}
          addedToCartMap={addedToCartMap}
        />
      );
    }

    if (homeLoading) {
      return (
        <main className="w-full flex-1 flex flex-col items-center pb-[72px] md:pb-0 justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </main>
      );
    }

    // Default Home Content
    return (
      <main className="w-full max-w-[1400px] mx-auto px-2 md:px-4 pt-2 md:pt-4 pb-[72px] md:pb-4 flex flex-col items-center">
        {/* Points Banner with Timer */}
        <div className="w-full mb-4 md:mb-6 animate-fade-in-up mt-1 md:mt-0 px-1">
          <div
            onClick={() => navigate('/points')}
            className={`relative w-full rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden transition-all border border-white/10 shadow-lg cursor-pointer hover:shadow-xl active:scale-[0.98]`}
          >
            <div className="absolute inset-0 z-0">
              <OptimizedImage
                src={cashbackBannerSrc}
                alt="Cashback Banner"
                priority={true}
                aspectRatio="4/1"
                width={1400}
                height={350}
                className="w-full h-full"
              />
            </div>
            <div className="relative z-10 flex items-center justify-between px-6 py-3 md:px-12 md:py-6" style={{ direction: 'rtl' }}>
              <div className="flex flex-col gap-0 md:gap-1 z-20">
                <p className="text-[9px] md:text-xs font-bold text-indigo-200/60 tracking-wider">
                  إجمالي نقاط هداياك المتاحة
                </p>
                <div className="flex items-center gap-2 md:gap-3">
                  <h2 className="text-xl md:text-4xl font-black text-white drop-shadow-lg tracking-tight">
                    نقاطك الحالية:
                  </h2>
                  <span className="text-lg md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-emerald-400 font-mono tracking-tight mt-0.5 opacity-90">
                    {profile?.pointsBalance || 0}
                  </span>
                </div>
              </div>
              <div className="relative group/icon hidden md:block">
                <div className="absolute inset-0 bg-white/10 rounded-full blur-md transform group-hover/icon:scale-125 transition-transform duration-500"></div>
                <div className="relative w-9 h-9 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-white/10 to-transparent border border-white/20 flex items-center justify-center backdrop-blur-md shadow-2xl">
                  <span className="text-indigo-100 font-black text-xs md:text-xl font-mono tracking-tighter opacity-90 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">RH</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Hero Slider (Ads from DB) */}
        {
          displayedAds.length > 0 && (
            <div className="relative w-full group mb-6 md:mb-10 group/slider mt-1 md:mt-0 px-2 md:px-0">
              <div className={`w-full p-2 md:p-4 rounded-[1.5rem] md:rounded-[2.5rem] backdrop-blur-md md:backdrop-blur-xl border transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-white/60'} ${neonGlowStyle}`}>
                <div className="w-full aspect-[2/1] md:aspect-[2.4/1] relative overflow-hidden rounded-[1.2rem] md:rounded-[2rem]">
                  {/* Slider Content */}
                  <div
                    className={`relative w-full h-full shadow-2xl select-none gpu-accel border border-white/5 rounded-[1.2rem] md:rounded-[2rem] ${isDark ? 'bg-black/40' : 'bg-slate-100/50'}`}
                    onMouseEnter={() => setIsHoveringHero(true)}
                    onMouseLeave={() => setIsHoveringHero(false)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="w-full h-full relative">
                      {displayedAds.map((slide, index) => {
                        let slideClass = '';
                        if (index === currentSlide) {
                          slideClass = 'translate-x-0 opacity-100 z-10';
                        } else if (index < currentSlide || (currentSlide === 0 && index === displayedAds.length - 1 && displayedAds.length > 1)) {
                          slideClass = '-translate-x-full opacity-0 z-0';
                        } else {
                          slideClass = 'translate-x-full opacity-0 z-0';
                        }
                        // Special case: when going from last to first
                        if (currentSlide === 0 && index === displayedAds.length - 1 && displayedAds.length > 1) {
                          slideClass = '-translate-x-full opacity-0 z-0';
                        }
                        return (
                          <div
                            key={slide.id}
                            className={`absolute inset-0 w-full h-full transition-all duration-700 ease-in-out transform gpu-accel ${slideClass}`}
                          >
                            <OptimizedImage
                              src={slide.image}
                              alt={slide.subTitle}
                              priority={index === 0}
                              objectFit={slide.imageFill === false ? 'contain' : 'cover'}
                              className="absolute inset-0 w-full h-full"
                            />

                            <div className="absolute inset-0 p-4 md:p-12 z-20 pointer-events-none">
                              {/* Tag */}
                              {!slide.hideSubTitle && (
                                <span
                                  className="absolute transition-all duration-700 text-white/90 font-black text-[10px] sm:text-xs md:text-xl tracking-wider uppercase bg-white/10 px-2.5 py-1 rounded-lg backdrop-blur-md border border-white/20 whitespace-nowrap z-30"
                                  style={{
                                    left: `${slide.tagPosition?.x || 10}%`,
                                    top: `${slide.tagPosition?.y || 35}%`,
                                    transform: 'translate(-50%, -50%)'
                                  }}
                                >
                                  {slide.subTitle}
                                </span>
                              )}

                              {/* Heading */}
                              {!slide.hideDiscount && (
                                <h2
                                  className="absolute transition-all duration-700 text-white font-black text-4xl sm:text-6xl md:text-[8.5rem] drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] leading-none tracking-tighter whitespace-nowrap z-20"
                                  style={{
                                    left: `${slide.headingPosition?.x || 10}%`,
                                    top: `${slide.headingPosition?.y || 52}%`,
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: 'clamp(1.5rem, 5vw, 8.5rem)'
                                  }}
                                >
                                  {slide.discount}
                                </h2>
                              )}

                              {/* Button */}
                              {!slide.hideButton && (
                                <div
                                  className="absolute transition-all duration-700 pointer-events-auto"
                                  style={{
                                    left: `${slide.buttonPosition?.x || 50}%`,
                                    top: `${slide.buttonPosition?.y || 80}%`,
                                    transform: 'translate(-50%, -50%)'
                                  }}
                                >
                                  <button
                                    onClick={() => slide.buttonLink && navigate(slide.buttonLink)}
                                    className="bg-white text-slate-900 px-4 md:px-12 py-2 md:py-4 rounded-full font-black text-[11px] sm:text-base md:text-2xl shadow-[0_10px_30px_rgba(255,255,255,0.4)] active:scale-90 hover:scale-105 transition-all hover:bg-slate-100 flex items-center gap-2 group/btn whitespace-nowrap"
                                  >
                                    {slide.buttonText}
                                    <ArrowRight size={20} className="transition-transform group-hover/btn:translate-x-1 hidden md:block" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation Arrows */}
                    {displayedAds.length > 1 && (
                      <div className="absolute inset-0 z-30 hidden md:flex items-center justify-between px-2 md:px-4 pointer-events-none opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePrevSlide(); }}
                          className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white transition-all active:scale-90 pointer-events-auto"
                        >
                          <ChevronRight size={24} className="md:w-8 md:h-8" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleNextSlide(); }}
                          className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white transition-all active:scale-90 pointer-events-auto"
                        >
                          <ChevronLeft size={24} className="md:w-8 md:h-8" />
                        </button>
                      </div>
                    )}
                    {/* Indicators */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-30">
                      {displayedAds.map((_, i) => (
                        <div
                          key={i}
                          onClick={(e) => { e.stopPropagation(); setCurrentSlide(i); }}
                          className={`w-2 h-2 md:w-3 md:h-3 rounded-full cursor-pointer transition-all ${currentSlide === i ? 'bg-white w-6 md:w-10' : 'bg-white/30 hover:bg-white/50'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {
          pageConfig && pageConfig.sections && pageConfig.sections.length > 0 ? (
            <div className="w-full flex flex-col gap-4 md:gap-12 mb-0 md:mb-12">
              {pageConfig.sections.sort((a, b) => a.order - b.order).map((section, idx) => {
                if (section.type === 'shops') {
                  let sectionStores = [];
                  if (section.filterCriteria.type === 'manual_ids' && section.filterCriteria.value.length > 0) {
                    sectionStores = allStores.filter(s => section.filterCriteria.value.includes(s.id.toString()));
                  } else if (section.filterCriteria.type === 'subCategory_name' && section.filterCriteria.value.length > 0) {
                    const allowedKeywords = section.filterCriteria.value.map(v => v.trim().toLowerCase());
                    sectionStores = allStores.filter(s => {
                      const searchStr = `${s.name} ${s.category || ''} ${(s.description || '')}`.toLowerCase();
                      return allowedKeywords.some(kw => searchStr.includes(kw));
                    });
                  } else {
                    sectionStores = [...allStores];
                  }

                  if (sectionStores.length === 0) return null;

                  return (
                    <div key={section.id} className="w-full mt-1 md:mt-4 mb-2 md:mb-4 flex flex-col items-center">
                      <div className="w-full flex items-center justify-between px-4 md:px-8 mb-4 max-w-[95%]" style={{ direction: 'rtl' }}>
                        <h2 className={`text-xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
                          {section.title} <span className="text-3xl md:text-5xl">🔥</span>
                        </h2>
                        {section.viewAllLink && (
                          <button
                            onClick={() => navigate(section.viewAllLink!)}
                            className="flex items-center gap-2 text-sm md:text-base font-black text-blue-500 hover:text-blue-600 transition-colors group"
                          >
                            شوف الكل
                            <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
                          </button>
                        )}
                      </div>
                      <div className={`relative w-[95%] rounded-[1.5rem] md:rounded-[2.5rem] p-3 md:p-6 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-white/30 border-white/40 shadow-sm'} backdrop-blur-0 md:backdrop-blur-md`}>
                        <div
                          className={`w-full overflow-x-auto no-scrollbar scroll-container cursor-grab active:cursor-grabbing overscroll-x-contain gpu-accel`}
                          onMouseDown={handleMouseDown}
                          onMouseLeave={handleMouseLeave}
                          onMouseUp={handleMouseUp}
                          onMouseMove={handleMouseMove}
                          style={{ direction: 'rtl' }}
                        >
                          <div className="flex items-center justify-start md:justify-center gap-3 md:gap-8 min-w-max mx-auto px-2 py-2">
                            {sectionStores.map((store, idx) => (
                              <div
                                key={`${store.id}-${idx}`}
                                className="flex flex-col items-center gap-2 md:gap-3 shrink-0 select-none group relative cursor-pointer"
                                style={{ width: 'max-content' }}
                                onClick={() => handleOpenStore(store)}
                              >
                                <div className={`relative w-[80px] h-[80px] sm:w-[120px] sm:h-[120px] md:w-[160px] md:h-[160px] rounded-2xl md:rounded-[2rem] flex items-center justify-center overflow-hidden transition-transform duration-300 ease-out hover:scale-105 border-2 ${!isDark ? 'bg-white border-slate-100 shadow-[0_8px_20px_rgba(0,0,0,0.05)]' : 'bg-[#1a1c1e] border-white/5 shadow-[0_8px_20px_rgba(0,0,0,0.3)]'}`}>
                                  <OptimizedImage
                                    src={store.logo}
                                    alt={store.name}
                                    aspectRatio="1/1"
                                    priority={section.order === 0}
                                    objectFit="cover"
                                    className="w-full h-full select-none pointer-events-none drop-shadow-md"
                                  />
                                </div>
                                <p className={`text-[11px] md:text-lg font-black text-center leading-tight mt-1 transition-colors ${isDark ? 'text-zinc-400 group-hover:text-white' : 'text-slate-600 group-hover:text-black'}`}>{store.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Products
                  let sectionProducts = [];
                  if (section.filterCriteria.type === 'manual_ids' && section.filterCriteria.value.length > 0) {
                    sectionProducts = allProducts.filter(p => section.filterCriteria.value.includes(String(p.id)));
                  } else if (section.filterCriteria.type === 'subCategory_name' && section.filterCriteria.value.length > 0) {
                    const allowedKeywords = section.filterCriteria.value.map(v => v.trim().toLowerCase());
                    sectionProducts = allProducts.filter(p => {
                      const searchStr = `${p.name} ${p.description || ''} ${p.category || ''} ${p.shopName || ''}`.toLowerCase();
                      return allowedKeywords.some(kw => searchStr.includes(kw));
                    });
                  } else {
                    sectionProducts = [...allProducts];
                  }

                  if (sectionProducts.length === 0) return null;

                  return (
                    <ProductSection
                      key={section.id}
                      title={section.title}
                      subtitle={section.footerText} // Footers can be subtitles
                      type="products"
                      layout={section.displayMode === 'grid' ? 'grid' : 'carousel'}
                      allProducts={sectionProducts}
                      allAds={ads}
                      viewAllLink={section.viewAllLink || `/page/${section.id}`}
                      hideViewAll={section.hideViewAll}
                      isDark={isDark}
                      priority={idx < 2} // Eager load the first two sections
                      onOpenCategory={handleOpenCategory}
                      onOpenDetails={openProductDetails}
                      onAddToCart={handleAddToCart}
                      addedToCartMap={addedToCartMap}
                    />
                  );
                }
              })}
            </div>
          ) : (
            <>
              {/* Store Marketplace (Featured Shops from DB) */}
              <div className="w-full mt-1 md:mt-4 mb-2 md:mb-4 flex flex-col items-center">
                <div className="w-full flex items-center justify-between px-4 md:px-8 mb-4 max-w-[95%]" style={{ direction: 'rtl' }}>
                  <h2 className={`text-xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
                    تعالا شوف محلات رشيد حالا <span className="text-3xl md:text-5xl">🔥</span>
                  </h2>
                  <button
                    onClick={() => navigate('/shops')}
                    className="flex items-center gap-2 text-sm md:text-base font-black text-blue-500 hover:text-blue-600 transition-colors group"
                  >
                    شوف الكل
                    <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
                  </button>
                </div>
                <div className={`relative w-[95%] rounded-[1.5rem] md:rounded-[2.5rem] p-3 md:p-6 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-white/30 border-white/40 shadow-sm'} backdrop-blur-0 md:backdrop-blur-md`}>
                  <div
                    ref={scrollContainerRef}
                    className={`w-full overflow-x-auto no-scrollbar scroll-container cursor-grab active:cursor-grabbing overscroll-x-contain gpu-accel`}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    style={{ direction: 'rtl' }}
                  >
                    <div className="flex items-center justify-start md:justify-center gap-3 md:gap-8 min-w-max mx-auto px-2 py-2">
                      {featuredShops.length > 0 ? (
                        featuredShops.map((store, idx) => (
                          <div
                            key={`${store.id}-${idx}`}
                            className="flex flex-col items-center gap-2 md:gap-3 shrink-0 select-none group relative cursor-pointer"
                            style={{ width: 'max-content' }}
                            onClick={() => handleOpenStore(store)}
                          >
                            <div className={`relative w-[80px] h-[80px] sm:w-[120px] sm:h-[120px] md:w-[160px] md:h-[160px] rounded-2xl md:rounded-[2rem] flex items-center justify-center overflow-hidden transition-transform duration-300 ease-out hover:scale-105 border-2 ${!isDark ? 'bg-white border-slate-100 shadow-[0_8px_20px_rgba(0,0,0,0.05)]' : 'bg-[#1a1c1e] border-white/5 shadow-[0_8px_20px_rgba(0,0,0,0.3)]'}`}>
                              <img src={store.logo} alt={store.name} draggable="false" decoding="async" fetchpriority="high" className={`w-full h-full object-cover select-none pointer-events-none drop-shadow-md`} />
                            </div>
                            <p className={`text-[11px] md:text-lg font-black text-center leading-tight mt-1 transition-colors ${isDark ? 'text-zinc-400 group-hover:text-white' : 'text-slate-600 group-hover:text-black'}`}>{store.name}</p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center w-full text-gray-500 font-bold">جاري تحميل المحلات...</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Home Sections */}
              <div className="w-full flex flex-col gap-2 md:gap-4 mb-0 md:mb-8">
                {homeSections.length > 0 ? (
                  homeSections.map((section) => (
                    <ProductSection
                      key={section.id}
                      title={section.title}
                      subtitle={section.subtitle}
                      hideTitle={section.hideTitle}
                      type={section.dataType}
                      layout={section.layout}
                      productIds={section.productIds}
                      productLimit={section.productLimit}
                      categoryId={section.categoryId}
                      adIds={section.adIds}
                      viewAllLink={section.viewAllLink}
                      hideViewAll={section.hideViewAll}
                      allProducts={allProducts.length > 0 ? allProducts : featuredProducts}
                      allAds={ads}
                      isDark={isDark}
                      onOpenCategory={handleOpenCategory}
                      onOpenDetails={openProductDetails}
                      onAddToCart={handleAddToCart}
                      addedToCartMap={addedToCartMap}
                    />
                  ))
                ) : (
                  /* Fallback if no sections configured in DB */
                  <ProductSection
                    title="منتجات مميزة"
                    type="products"
                    layout="carousel"
                    allProducts={featuredProducts}
                    allAds={ads}
                    isDark={isDark}
                    onOpenCategory={handleOpenCategory}
                    onOpenDetails={openProductDetails}
                    onAddToCart={handleAddToCart}
                    addedToCartMap={addedToCartMap}
                  />
                )}
              </div>
            </>
          )
        }

        {/* Main Footer */}
        <div ref={footerRef} className="w-full mt-auto">
          <Footer isDark={isDark} />
        </div>
      </main >
    );
  };

  return (
    <div className={`min-h-[100dvh] w-full transition-colors duration-300 ${isDark ? 'bg-[#0f1115]' : 'bg-[#f5f6f7]'} relative flex flex-col page-transition`}>



      {/* Main Content Container - Fixed for Cart, Standard for others */}
      {/* Fixed padding-top to prevent content hidden behind header - Modified for safe area */}
      <div className={`w-full transition-all duration-300 ${pageView === 'cart' ? `fixed inset-0 z-40 pt-[calc(7rem+env(safe-area-inset-top))] md:pt-36 h-[100dvh] ${isDark ? 'bg-[#0f1115]' : 'bg-[#f5f6f7]'}` : 'pt-[calc(5rem+env(safe-area-inset-top))] md:pt-28 flex-1 flex flex-col'}`}>
        {renderContent()}
      </div>

      <Toast message="تم إضافة المنتج" isVisible={showToast} onClose={() => setShowToast(false)} isDark={isDark} />
      <ProfileModal isOpen={showProfileModal} onClose={closeProfile} isDark={isDark} userAvatar={userAvatar} userName={userName} userEmail={profile?.email} userPhone={profile?.phone} onLogout={onLogout} />
      {selectedStore && <StoreDetailsPage store={selectedStore} products={[]} isDark={isDark} onClose={() => setSelectedStore(null)} onOpenDetails={openProductDetails} onAddToCart={handleAddToCart} addedToCartMap={addedToCartMap} />}
      {/* Product Details Modal Wrapper - Fixed padding-top to prevent content hidden */}
      {selectedProduct && <div className={`fixed inset-0 z-[100] w-full h-full overflow-y-auto custom-scrollbar pt-24 md:pt-32 overscroll-y-contain animate-fade-in-scale ${isDark ? 'bg-[#0f1115]' : 'bg-[#f8f9fa]'}`} style={{ WebkitOverflowScrolling: 'touch' }}><ProductDetailsModal product={selectedProduct} isDark={isDark} onClose={closeProductDetails} onAddToCart={handleAddToCart} onBuyNow={handleBuyNow} onOpenDetails={openProductDetails} addedToCartMap={addedToCartMap} user={user} isAdmin={profile?.role === 'admin'} /></div>}

      {/* Render SearchPage if on search route */}
      {location.pathname === '/search' && (
        <>
          <div className="fixed inset-0 z-40 pt-20 md:pt-28 overflow-y-auto">
            <SearchPage
              isDark={isDark}
              onOpenDetails={openProductDetails}
              onAddToCart={handleAddToCart}
              addedToCartMap={addedToCartMap}
            />
          </div>
          <div className="fixed bottom-0 left-0 right-0 z-40">
            <Footer isDark={isDark} />
          </div>
        </>
      )}


      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}; border-radius: 10px; }
        @keyframes fade-in-scale { 0% { opacity: 0; transform: scale(0.96); } 100% { opacity: 1; transform: scale(1); } }
        .animate-fade-in-scale { animation: fade-in-scale 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        @keyframes fade-in-up { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.7s ease-out forwards; }
        /* ... other styles ... */
      `}</style>
    </div>
  );
};

export default HomePage;
